/**
 * Sends a visitor who has not joined URC Loyalty away from a page built for members.
 *
 * The editor sets an optional page on `acf/loyalty-profile` or `acf/loyalty-profile-header`
 * (App\Loyalty\NonMemberRedirect) and Blade publishes it as `data-loyalty-non-member-redirect`.
 * No field set, no attribute, and this module returns having done nothing at all.
 *
 * **Nothing is decided in PHP, and cannot be.** Whether a fan has joined is a loyalty API read,
 * and these pages sit behind Varnish and the edge, so a server-side redirect would be computed for
 * one visitor and served to the next. Same defence as ./navLink.js and the join strip.
 *
 * **It adds no requests.** The verdict comes from the memoised gate chain (./toasts/gate.js) that
 * the toast layer already runs on every page -- one subscriber, however many blocks are on the
 * page -- so a profile page costs whatever that chain already cost. (What that is has changed twice
 * since this module was written: the session call went with D-063, and gate 2.5 added one consent
 * POST per tab per TTL. Neither is this module's.)
 *
 * **Front end only.** The same markup is server-rendered into the block editor, where a navigation
 * would take an editor off an unsaved post, so `isEditorMarkup()` below bails before the chain is
 * even started.
 *
 * See docs/subsystems/gutenberg-blocks.md, D-062 and D-083.
 */

import { VERDICT, whenToastEligible } from './toasts/gate';

/**
 * The attribute a loyalty block publishes its editor's choice into.
 *
 * A data attribute rather than a key inside `data-loyalty-props`, and that is a contract point
 * rather than a preference: everything in the props envelope except its three routing keys is
 * forwarded to the block's component as a prop (see ../loyalty.jsx), and this is not a prop of
 * anything. It also has to be readable whether or not a component ever mounted.
 */
const SELECTOR = '[data-loyalty-non-member-redirect]';

/**
 * The verdicts that mean "this browser cannot use a member page".
 *
 * **The line through the gate chain's vocabulary is a definite answer about the FAN against a failed
 * READ, not "no" against "yes".** Everything in the list is the former, and every absentee is the
 * fail-open posture (deliberately not counted here -- the count is what went stale last time):
 *
 * `armed`           -- a member. Nothing to do.
 * `unknown`         -- the enrolment read FAILED. On a host whose origin is off the gateway's CORS
 *                      allowlist every read fails this way, and redirecting there would bounce every
 *                      member off their own profile for as long as the outage lasts. This is the one
 *                      place this module disagrees with ./navLink.js, which treats `unknown` as
 *                      not-a-member -- and the difference is the cost of being wrong. Repointing a
 *                      link that the fan may not click is cheap; a navigation they did not ask for
 *                      is not.
 * `consent-unknown` -- the CONSENT read failed, for exactly the same reasons and with exactly the
 *                      same cost: an Auth0 Management outage, a misconfigured install, or the
 *                      surface switched off by its config flag. A member is still a member through
 *                      all three.
 * `unconfigured`    -- the tenant runs no loyalty programme, so nobody can be a member and the
 *                      target page has nothing to offer either. An environment fault, not a fan
 *                      state.
 * `off`             -- no gateway at all; the question was never asked.
 *
 * Two members of the list look adjacent to those and are not, because both are definite answers
 * rather than failed reads:
 *
 * `no-session`   -- signed in with no live loyalty token. Every self-scoped request on the page 401s
 *                   before the wire, so there is no version of this page that works for them, and
 *                   the page they land on offers the sign-in that fixes it.
 * `no-consent`   -- signed in, live token, consent WITHHELD. Same shape: ./consentGate.js refuses
 *                   every fan-scoped call before the wire, so this page can only render errors.
 *                   **Added 2026-09-10, and its absence was a live bug** -- gate 2.5 arrived on
 *                   2026-09-03 and this list was not revisited, so from that day the one cohort the
 *                   field exists for, a signed-in fan who has not joined, was the one cohort that
 *                   was never moved. It is only sound to act on now because ../toasts/gate.js
 *                   settles `consent-unknown` for an unreadable answer instead of folding it in
 *                   here; the collapsed verdict could not be acted on either way. See D-062 and
 *                   D-083.
 */
const REDIRECT_ON = [
	VERDICT.SIGNED_OUT,
	VERDICT.NO_SESSION,
	VERDICT.NO_CONSENT,
	VERDICT.NOT_ENROLLED,
];

/**
 * Whether this element is the block editor's copy of the block rather than the front end's.
 *
 * **The one state in which this module must do nothing at all, whatever the fan's verdict.** ACF
 * blocks are server-rendered into the post editor, so the same markup -- the same wrapper, carrying
 * the same target -- exists in wp-admin, and `window.location.replace()` there navigates an editor
 * away from a post they may not have saved. There is no verdict worth that.
 *
 * The flag is `editor` inside `data-loyalty-props`, which the Blade views put on the SAME wrapper as
 * the target (see resources/views/blocks/loyalty-profile{,-header}.blade.php), so this costs one
 * attribute read on an element already in hand and works whether or not anything mounted.
 *
 * **Parsed, not substring-matched.** `raw.includes( '"editor":true' )` would be shorter and would
 * depend on PHP's JSON spacing, on key order, and on no other key ever ending in `editor`.
 *
 * **Deliberately a second, tiny reader rather than importing `readProps()` from ../loyalty.jsx.**
 * That entry imports this module, so importing back would close a cycle -- and the entry's version
 * wants the whole envelope where this wants one boolean. It fails CLOSED on unreadable JSON, unlike
 * the entry's, because "I cannot tell whether this is the editor" must not authorise a navigation.
 *
 * @param {Element} element The wrapper carrying the target.
 * @return {boolean} True in the editor, and true when the props cannot be read at all.
 */
function isEditorMarkup( element ) {
	const raw = element.dataset.loyaltyProps;

	if ( ! raw ) {
		// Every loyalty block emits this attribute unconditionally, so its absence means the markup
		// is not what this module thinks it is. Treated as the editor for the same reason as a parse
		// failure below.
		return true;
	}

	try {
		return Boolean( JSON.parse( raw ).editor );
	} catch ( error ) {
		console.warn( '[loyalty:redirect] unreadable block props; not redirecting', error );

		return true;
	}
}

/**
 * Publish what happened, on <html>, the way ./toasts/gate.js publishes its verdict.
 *
 * The whole of the debugging story for "why was I bounced" and, more often, "why was I not".
 *
 * @param {string} state The verdict, or a reason the chain was never asked.
 * @return {void}
 */
function mark( state ) {
	document.documentElement.dataset.loyaltyRedirect = state;
}

/**
 * Arm the redirect for whichever block on this page carries a target.
 *
 * Called once from ../loyalty.jsx. Safe on a page with no loyalty block and safe before the gate
 * has an answer; both cost one querySelector and nothing else.
 *
 * @return {void}
 */
export function initNonMemberRedirect() {
	// querySelector, SINGULAR, and that is the rule rather than an optimisation: it returns the
	// first match in document order, so a page carrying several blocks with DIFFERENT targets is a
	// defined outcome -- the topmost one wins and the rest are never read -- instead of a conflict
	// to reconcile once the verdict lands. Resolving it here, before anything is asked, is what
	// makes the choice depend on the markup alone rather than on which block mounted first.
	//
	// Note ./navLink.js does the opposite with querySelectorAll, because there every match has to
	// be rewritten. Here exactly one destination can be acted on.
	//
	// A block whose field is blank emits no attribute at all, so an empty block above a filled one
	// cannot capture the selector.
	const source = document.querySelector( SELECTOR );
	const url = source && source.dataset.loyaltyNonMemberRedirect;

	// No block on this page offers one. An editorial state, not a fault: nothing is subscribed and
	// the gate chain is not started on this account.
	if ( ! url ) {
		return;
	}

	// Which element supplied the target, for a page where more than one could have.
	source.dataset.loyaltyNonMemberRedirectSource = 'chosen';

	// The editor, where a navigation would cost somebody an unsaved post. Bailing HERE rather
	// than inside the subscriber is the point: the gate chain is never started, so wp-admin buys
	// no consent POST and no enrolment read on every one of ACF's fetch-block renders. The cost is
	// that `editor` is published instead of a verdict -- which is the more useful answer anyway,
	// because "why was I not bounced" here is answered by the surface, not by the fan.
	//
	// ../loyalty.jsx already declines to MOUNT on this same flag. Only the path that navigates
	// was missing the check, which is the wrong way round.
	if ( isEditorMarkup( source ) ) {
		mark( 'editor' );

		return;
	}

	whenToastEligible( verdict => {
		mark( verdict );

		if ( ! REDIRECT_ON.includes( verdict ) ) {
			return;
		}

		// replace(), not href: this page is one the visitor may not use, so leaving it in the
		// history means Back returns here and bounces them straight out again.
		window.location.replace( url );
	} );
}
